'use client';

import { AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/lib/supabase/client';
import { trackEvent } from '@/lib/posthog';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { toast } from 'sonner';

/**
 * Non-dismissible banner shown when a user's subscription payment has failed.
 * Links to the Stripe Customer Portal so they can update their card.
 */
export function PaymentFailedBanner() {
  const { isPastDue, subscription } = useSubscription();
  const [isLoading, setIsLoading] = useState(false);

  if (!isPastDue) return null;

  const handleUpdatePayment = async () => {
    setIsLoading(true);

    trackEvent('payment_failed_banner_clicked', {
      tier: subscription.tier,
      subscription_id: subscription.id,
    });

    try {
      const { data, error } = await (supabase as any).functions.invoke('create-billing-portal', {
        body: { return_url: window.location.href },
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (err) {
      console.error('Failed to open billing portal:', err);
      toast.error('Could not open billing settings. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div
      className={cn(
        'flex items-center gap-3 mx-4 mt-2 rounded-lg border px-4 py-2.5',
        'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/50'
      )}
      role="alert"
    >
      <AlertTriangle className="h-4 w-4 shrink-0 text-red-500 dark:text-red-400" />

      <p className="flex-1 text-sm font-medium text-red-800 dark:text-red-200">
        Payment failed &mdash; update your card to keep access
      </p>

      <Button
        variant="outline"
        size="sm"
        onClick={handleUpdatePayment}
        disabled={isLoading}
        className={cn(
          'shrink-0',
          'bg-red-600 text-white hover:bg-red-700 border-red-600',
          'dark:bg-red-700 dark:hover:bg-red-800 dark:border-red-700'
        )}
      >
        {isLoading ? 'Opening...' : 'Update payment'}
        {!isLoading && <ArrowRight className="ml-1 h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}

export default PaymentFailedBanner;
